// 部署自愈：老会话去加载一个已经被新版本换掉的 hash 资源时，让它自己刷新一次。
//
// 线上的失败形态（2026-08-19 对 admin.yepsavings.com 实测，不是照抄网上的写法）：
// nginx 的 `try_files $uri $uri/ /index.html` 会把不存在的 /js/xxx.js 回成
// 200 + Content-Type: text/html（那 717 字节的 index.html），而不是 404；再叠加
// server 级的 `X-Content-Type-Options: nosniff`，浏览器拒绝把它当脚本/样式执行，
// 于是在 <script> / <link> 上触发 error 事件。所以判据不能写「404」。
//
// webpack 5.109.2 打出来的两处运行时（从 dist/js/app.*.js 里读出来的）：
//   JS ：message = "Loading chunk 3524 failed.\n(error: https://…/js/3524.abc.js)"
//        name    = "ChunkLoadError"，type = "missing" | "error"，request = 那个 URL
//   CSS：message = "Loading CSS chunk 3524 failed.\n(error: …)"
//        name    = "ChunkLoadError"，code = "CSS_CHUNK_LOAD_FAILED"，request = 那个 URL
// 这个版本两者都设了 name，所以认 name 就够；code 与正则是兜底——早期 webpack 5
// 不给 CSS 错误设 name，将来升级依赖形态漂了也还能兜住。
// （实测里真正抵达 router.onError 的往往是 CSS 那条，只认 JS 的正则会整个失灵。）
//
// 防循环的两道锁，缺一不可：
//   1. 同一个失败资源 URL 一辈子只刷一次。刷完还是同一个 URL 挂，说明不是「版本换了」
//      而是资源真的取不到，再刷多少次都一样。
//   2. 每个标签页刷新次数有硬上限。就算某天 hash 命名策略变了、URL 每次都不同，
//      也不会转起来 —— 线上资源要是持续挂，无限刷新比白屏更糟。
// 两道锁合起来的上界：一个标签页最多刷 MAX_RELOADS 次，且每次发布最多贡献一次
// （一次页面加载里 index.html 钉死了那一组 chunk URL，要出现新 URL 必须又发布了一次）。

var CHUNK_RELOAD_STATE = 'admin:chunk-reloaded'

// 实测部署频率约 6.5 次/天，而服务器侧还保留了 7 天的旧资源，正常情况下根本走不到
// 这里。5 次是纯兜底，够覆盖「一个标签页跨几次发布」，又把最坏情况钉死。
var MAX_RELOADS = 5

var CHUNK_MESSAGE_RE = /Loading (?:CSS )?chunk\s+\S+\s+failed/i

function isChunkLoadError(error) {
  if (!error) return false
  if (error.name === 'ChunkLoadError') return true
  if (error.code === 'CSS_CHUNK_LOAD_FAILED') return true
  return CHUNK_MESSAGE_RE.test(String(error.message || ''))
}

// 用哪个资源来分桶。两处运行时都会填 request；填不上时退回 message —— 它里面同样
// 带着 chunk id 和 URL，作为去重键足够稳定。截断是为了别让存储无界增长。
function chunkKey(error) {
  var raw = (error && (error.request || error.message)) || ''
  return String(raw).slice(0, 200)
}

// 访问 window.sessionStorage 本身就可能抛（浏览器禁掉存储时）。
function getSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch (e) {
    return null
  }
}

// 读不出可信状态时一律返回 null，调用方据此走「只提示不刷新」。
// 宁可不刷，也不能在拿不到防重入凭据的浏览器上转圈。
function readState(storage) {
  if (!storage) return null
  var raw
  try {
    raw = storage.getItem(CHUNK_RELOAD_STATE)
  } catch (e) {
    return null
  }
  if (raw === null || raw === undefined) return { n: 0, urls: [] }
  // 旧版本存的是字符串 '1'：当成「已经刷过一次，但不知道是哪个资源」
  if (raw === '1') return { n: 1, urls: [] }
  var parsed
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    return null
  }
  if (!parsed || typeof parsed.n !== 'number' || parsed.n < 0 || !Array.isArray(parsed.urls)) return null
  return { n: parsed.n, urls: parsed.urls }
}

// 写完必须回读确认。写不进去（配额满、隐私模式静默失败）就等于没有防重入的凭据。
function writeState(storage, state) {
  if (!storage) return false
  var payload = JSON.stringify(state)
  try {
    storage.setItem(CHUNK_RELOAD_STATE, payload)
    return storage.getItem(CHUNK_RELOAD_STATE) === payload
  } catch (e) {
    return false
  }
}

// 返回值给测试和调用方判断用：
//   'ignored'  不是 chunk 错误，交回原来的处理
//   'reloaded' 这个资源本标签页第一次挂，且没到上限，已触发刷新
//   'notified' 同一资源刷过了 / 到上限了 / 状态存不住，只提示，绝不再刷
function handleChunkLoadError(error, deps) {
  if (!isChunkLoadError(error)) return 'ignored'
  var state = readState(deps.storage)
  var key = chunkKey(error)
  if (state && state.n < MAX_RELOADS && state.urls.indexOf(key) === -1) {
    // urls 只在真正刷新时才追加，所以它的长度天然被 MAX_RELOADS 卡住，不会无界增长。
    if (writeState(deps.storage, { n: state.n + 1, urls: state.urls.concat([key]) })) {
      deps.reload()
      return 'reloaded'
    }
  }
  deps.notify()
  return 'notified'
}

// router.onError 的回调。依赖全部注入，好在 node:test 里不带浏览器跑。
function createRouterErrorHandler(deps) {
  return function onRouterError(error) {
    // 导航被中断，afterEach 不会跑，进度条会一直吊在页面顶上。
    deps.done()
    // 无论哪条分支都把原始错误打出来。注册了 onError 之后 vue-router 就不再自己
    // console.error 了；要是 chunk 错误也一并吞掉，「刷完还失败」「存储用不了」
    // 「资源永久损坏」这几种真正需要人介入的情况在控制台和监控里会完全看不见。
    deps.log(error)
    return handleChunkLoadError(error, deps)
  }
}

module.exports = {
  CHUNK_RELOAD_STATE: CHUNK_RELOAD_STATE,
  MAX_RELOADS: MAX_RELOADS,
  isChunkLoadError: isChunkLoadError,
  chunkKey: chunkKey,
  getSessionStorage: getSessionStorage,
  handleChunkLoadError: handleChunkLoadError,
  createRouterErrorHandler: createRouterErrorHandler,
}
